import { useState, useEffect } from 'react';
import {
  MapContainer, TileLayer, Polygon, CircleMarker,
  Tooltip, useMapEvents, useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

export const CATEGORIES = [
  { key: 'gate',      label: 'Gate / Access',  color: '#f59e0b', emoji: '🚪' },
  { key: 'water',     label: 'Water Source',   color: '#3b82f6', emoji: '💧' },
  { key: 'hazard',    label: 'Hazard',         color: '#ef4444', emoji: '⚠️' },
  { key: 'livestock', label: 'Livestock Pen',  color: '#22c55e', emoji: '🐄' },
  { key: 'building',  label: 'Building',       color: '#22c55e', emoji: '🏠' },
];

export const DESCRIPTIONS = {
  gate:      'Mark where entry gates can be found. If they are locked (electronically or with a key) please provide information on how to gain access.',
  water:     'Wells, ponds, or other sources of water for fire/EMS operations.',
  hazard:    'Fuel/propane tanks, underground pipelines, uncapped wells, sinkholes, etc.',
  livestock: 'All livestock locations — barns, pens, confinements.',
  building:  'Homes, grain bins, equipment sheds.',
};

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length >= 2) map.fitBounds(positions, { padding: [50, 50] });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function PinLayer({ active, onPlace }) {
  useMapEvents({
    click(e) {
      if (active) onPlace({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

/**
 * Shared feature-pinning form.
 *
 * Props:
 *   property    – { name, description, notes, boundary, features? }
 *   stepLabel   – badge text, e.g. "Step 2 of 2" or "Edit: Step 2 of 2"
 *   saveLabel   – primary button text
 *   skipLabel   – secondary button text (null = hide)
 *   onSave(updatedProperty)  – called with the full property + pins
 *   onSkip()                 – called when user skips (optional)
 */
export default function PropertyFeaturesForm({
  property,
  stepLabel = 'Step 2 of 2',
  saveLabel = 'Save & Finish',
  skipLabel = 'Skip for Now',
  onSave,
  onSkip,
  disabled = false,
}) {
  const boundary  = property?.boundary ?? [];
  const positions = boundary.map(p => [p.lat, p.lng]);
  const cat       = (key) => CATEGORIES.find(c => c.key === key);

  const [activeCat,    setActiveCat]    = useState(null);
  const [pins,         setPins]         = useState(property?.features ?? []);
  const [pendingPin,   setPendingPin]   = useState(null);
  const [pendingLabel, setPendingLabel] = useState('');
  const [pendingNotes, setPendingNotes] = useState('');

  const handlePlace = ({ lat, lng }) => {
    setPendingPin({ lat, lng, category: activeCat });
    setPendingLabel('');
    setPendingNotes('');
  };

  const handleConfirm = () => {
    if (!pendingLabel.trim()) return;
    setPins(prev => [
      ...prev,
      { id: Date.now(), ...pendingPin, label: pendingLabel.trim(), notes: pendingNotes.trim() },
    ]);
    setPendingPin(null);
  };

  const handleSave = () => {
    onSave({ ...property, features: pins });
  };

  return (
    <div className="features-container">

      <div className="features-header">
        <div className="features-header-text">
          <h1>Add Property Features</h1>
          <p>{property?.name ? `Mark key locations on ${property.name}.` : 'Mark key locations on your property.'}</p>
        </div>
        {stepLabel && <span className="step-badge">{stepLabel}</span>}
      </div>

      <div className="cat-bar">
        <p className="cat-bar-label">Select a feature type, then click the map to place it:</p>
        <div className="cat-chips">
          {CATEGORIES.map(c => (
            <button
              key={c.key}
              className={`cat-chip${activeCat === c.key ? ' active' : ''}`}
              style={{ '--cc': c.color }}
              onClick={() => setActiveCat(prev => prev === c.key ? null : c.key)}
            >
              {c.emoji} {c.label}
            </button>
          ))}
        </div>
        {activeCat && (
          <div className="place-hint">
            <p>Click anywhere on the map to place a <strong>{cat(activeCat)?.label}</strong> marker.</p>
            {DESCRIPTIONS[activeCat] && (
              <p className="place-hint-description">{DESCRIPTIONS[activeCat]}</p>
            )}
          </div>
        )}
      </div>

      {pendingPin && (
        <div className="pin-form">
          <p className="pin-form-title">{cat(pendingPin.category)?.emoji} New {cat(pendingPin.category)?.label}</p>
          <input
            type="text"
            placeholder="Label (e.g., Well, Red Barn, Grain Bin)"
            value={pendingLabel}
            onChange={e => setPendingLabel(e.target.value)}
            autoFocus
          />
          <textarea
            rows={2}
            placeholder="Notes (e.g., fuel/propane tank, seasonal stream)"
            value={pendingNotes}
            onChange={e => setPendingNotes(e.target.value)}
          />
          <div className="pin-form-actions">
            <button className="btn-confirm-pin" onClick={handleConfirm} disabled={!pendingLabel.trim()}>Add Marker</button>
            <button className="btn-discard-pin" onClick={() => setPendingPin(null)}>Cancel</button>
          </div>
        </div>
      )}

      {pins.length > 0 && (
        <div className="pin-list">
          <h2>Placed Features ({pins.length})</h2>
          {pins.map(pin => (
            <div key={pin.id} className="pin-row">
              <span className="pin-dot" style={{ background: cat(pin.category)?.color }} />
              <div className="pin-row-text">
                <span className="pin-row-label">{cat(pin.category)?.emoji} {pin.label}</span>
                {pin.notes && <span className="pin-row-notes">{pin.notes}</span>}
                <span className="pin-row-cat">{cat(pin.category)?.label}</span>
              </div>
              <button className="pin-delete" onClick={() => setPins(prev => prev.filter(p => p.id !== pin.id))}>✕</button>
            </div>
          ))}
        </div>
      )}

      <div className={`features-map-wrap${activeCat ? ' placing' : ''}`}>
        <MapContainer center={[41.5, -99.9]} zoom={7} style={{ height: '460px', width: '100%' }} scrollWheelZoom={true}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {positions.length >= 2 && <FitBounds positions={positions} />}
          <PinLayer active={activeCat} onPlace={handlePlace} />
          {positions.length >= 3 && (
            <Polygon positions={positions}
              pathOptions={{ color: '#fe3130', fillColor: '#fe3130', fillOpacity: 0.1, weight: 2 }} />
          )}
          {pins.map(pin => (
            <CircleMarker key={pin.id} center={[pin.lat, pin.lng]} radius={9}
              pathOptions={{ color: cat(pin.category)?.color, fillColor: cat(pin.category)?.color, fillOpacity: 0.9, weight: 2 }}>
              <Tooltip direction="top" offset={[0, -10]}>
                <strong>{cat(pin.category)?.emoji} {pin.label}</strong>
                {pin.notes && <><br /><span>{pin.notes}</span></>}
              </Tooltip>
            </CircleMarker>
          ))}
          {pendingPin && (
            <CircleMarker center={[pendingPin.lat, pendingPin.lng]} radius={9}
              pathOptions={{ color: cat(pendingPin.category)?.color, fillColor: cat(pendingPin.category)?.color, fillOpacity: 0.4, weight: 2, dashArray: '5,5' }} />
          )}
        </MapContainer>
      </div>

      <div className="features-actions">
        <button className="btn-save" onClick={handleSave} disabled={disabled}>{saveLabel}</button>
        {onSkip && skipLabel && (
          <button className="btn-skip" onClick={onSkip} disabled={disabled}>{skipLabel}</button>
        )}
      </div>

    </div>
  );
}

